/**
 * Load-time normalisation so the drill canvas renders EVERY drill — including
 * legacy ones (older library drills, and anything the Brain's `draft_drill`
 * tool authors) that store players as `entities` rather than `rolePlayers`.
 *
 * The 2D canvas renders:
 *   - players  only from `rolePlayers` (PlayerRoleOverlay2D)
 *   - arrows   only from POINT-anchored endpoints (DrawingOverlay2D falls back
 *              to pitch-centre for `{kind:'entity'}` anchors)
 *   - playback only from v2 keyframes (`rolePlayers` map), not v1 `positions`
 *
 * So a legacy spec shows cones/zones but no players, collapsed arrows, and no
 * animation. These pure helpers project the legacy shape into what the canvas
 * renders. They're no-ops for specs already authored in the canvas (v2).
 */
import type { DrillArrow, DrillEntity, DrillKeyframe, NormPoint } from '@4cm/core/drills/types';
import type { DrillRolePlayer } from './playerRoles';

/** Legacy entity.type → drill-canvas role id. */
const ROLE_FOR_ENTITY_TYPE: Record<string, string> = {
  home: 'attacker',
  away: 'defender',
  ball: 'ball',
  official: 'coach',
};

/** Project legacy `entities` into `rolePlayers`, keeping the same id so any
 *  entity-anchored arrows / keyframes still resolve against them. */
export function entitiesToRolePlayers(entities: readonly DrillEntity[]): DrillRolePlayer[] {
  return entities.map((e) => {
    const label = typeof e.label === 'string' && e.label.length > 0 ? e.label : undefined;
    return {
      id: e.id,
      roleId: ROLE_FOR_ENTITY_TYPE[e.type] ?? 'attacker',
      position: e.position,
      ...(label ? { label } : {}),
    };
  });
}

/** Resolve `{kind:'entity'}` arrow endpoints to fixed points using the players'
 *  (or entities') positions, so DrawingOverlay2D draws them. Unresolvable
 *  anchors are left untouched; point anchors pass through unchanged (no-op for
 *  canvas-authored arrows). */
export function resolveArrowAnchors(
  arrows: readonly DrillArrow[],
  players: readonly { id: string; position: NormPoint }[],
  entities: readonly DrillEntity[] = [],
): DrillArrow[] {
  const pos = new Map<string, NormPoint>();
  for (const e of entities) pos.set(e.id, e.position);
  for (const p of players) pos.set(p.id, p.position); // players take precedence
  const resolve = (ep: DrillArrow['from']): DrillArrow['from'] => {
    if (ep.kind !== 'entity') return ep;
    const at = pos.get(ep.entityId);
    return at ? { kind: 'point', position: at } : ep;
  };
  return arrows.map((a) => ({ ...a, from: resolve(a.from), to: resolve(a.to) }));
}

/** Copy v1 entity-keyed `positions` into the v2 `rolePlayers` map (ids match
 *  after projection) so legacy keyframe animations play. */
export function liftLegacyKeyframes(keyframes: readonly DrillKeyframe[]): DrillKeyframe[] {
  return keyframes.map((kf) =>
    kf.rolePlayers || Object.keys(kf.positions ?? {}).length === 0
      ? kf
      : { ...kf, rolePlayers: kf.positions },
  );
}
